import { useState } from "react";
import { brand, navItems, categories } from "@/mocks/gua";

const FORM_ADDR = "https://readdy.ai/api/form/daf97kjep053ijub1li0";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("contact_alt") || "").trim();
    setSending(true);
    setMsg("");

    if (honeypot) {
      setSending(false);
      setStatus("success");
      form.reset();
      setEmail("");
      setMsg("订阅成功！感谢关注，最新深扒会第一时间送达。");
      return;
    }

    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (responseText || "请求失败，请稍后再试");
      const isSpam =
        (parsed?.code && parsed.code !== "OK") ||
        /spam/i.test(serverMsg) ||
        !res.ok ||
        (parsed?.code !== "OK" && responseText && !res.ok);

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
        setEmail("");
        setMsg("订阅成功！感谢关注，最新深扒会第一时间送达。");
      } else {
        setStatus("error");
        setMsg(isSpam ? "提交未通过安全校验，请检查后重试。" : serverMsg);
      }
    } catch {
      setStatus("error");
      setMsg("网络异常，订阅未成功，请稍后再试。");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="newsletter" className="mt-20 scroll-mt-24 md:mt-28">
      {/* 订阅 CTA */}
      <div className="px-4 md:px-6">
        <div className="mx-auto max-w-[1240px]">
          <div className="overflow-hidden rounded-xl bg-primary-700 px-5 py-8 md:px-10 md:py-10">
            <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2">
              <div>
                <h2 className="font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
                  热点常在变，但「看懂的姿势」可以一直进步
                </h2>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-background-50/80">
                  每周精选 3 篇深度解读与完整时间线，第一时间推送给你。免费订阅，随时可退。
                </p>
              </div>
              <form onSubmit={handleSubmit} data-readdy-form="瓜情局-深度吃瓜订阅" className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入你的邮箱，订阅深度吃瓜"
                  autoComplete="off"
                  className="w-full rounded-md bg-background-50/95 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-accent-400"
                />
                {/* honeypot */}
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-guard"
                />
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:opacity-70"
                >
                  {sending ? "提交中…" : "立即订阅"}
                  {!sending && <i className="ri-send-plane-line" />}
                </button>
              </form>
            </div>
            {status !== "idle" && (
              <p
                className={`mt-4 max-w-2xl text-sm ${
                  status === "success" ? "text-accent-200" : "text-secondary-300"
                }`}
              >
                {msg}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 页脚主体 */}
      <div className="mt-6 bg-primary-900">
        <div className="mx-auto grid max-w-[1240px] grid-cols-2 gap-8 px-4 py-12 md:grid-cols-4 md:px-6">
          <div className="col-span-2 md:col-span-1">
            <a href="#home" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-goblet-fill text-lg" />
              </span>
              <span className="font-heading text-lg font-bold text-background-50">{brand.name}</span>
            </a>
            <p className="mt-3 max-w-xs text-xs leading-relaxed text-background-50/65">
              {brand.slogan}。深度解读、来龙去脉、背景分析、各方回应与舆论风向，一次讲透。
            </p>
            <div className="mt-4 flex items-center gap-2">
              {["ri-weibo-line", "ri-wechat-line", "ri-mail-line", "ri-rss-line"].map((ic) => (
                <a
                  key={ic}
                  href="#newsletter"
                  aria-label="订阅入口"
                  className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/10 text-background-50/80 transition-colors hover:bg-background-50/20"
                >
                  <i className={ic} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-background-50">快速导航</h3>
            <ul className="flex flex-col gap-2 text-sm">
              {navItems.map((n) => (
                <li key={n.key}>
                  <a href={n.href} className="text-background-50/70 transition-colors hover:text-background-50">
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-background-50">热评栏目</h3>
            <ul className="flex flex-col gap-2 text-sm">
              {categories.slice(0, 5).map((c) => (
                <li key={c.name}>
                  <a href="#column" className="text-background-50/70 transition-colors hover:text-background-50">
                    {c.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-background-50">联系我们</h3>
            <p className="flex items-center gap-2 text-sm text-background-50/70">
              <i className="ri-mail-line" /> {brand.email}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-background-50/55">
              投稿 / 线索：editor@guajustory.example
              <br />
              内容纠错：proof@guajustory.example
            </p>
          </div>
        </div>
        <div className="border-t border-background-50/10">
          <div className="mx-auto flex max-w-[1240px] flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-background-50/50 sm:flex-row md:px-6">
            <p>© 2026 {brand.name} 深度吃瓜平台 · 本站内容为虚构演示，不代表真实事件与观点</p>
            <p className="flex items-center gap-1">
              <i className="ri-leaf-line text-accent-400" /> 理性吃瓜，拒绝造谣带节奏
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}